import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Gamepad2, Globe, Shield, Headphones, HardDrive, Server, Monitor,
  Cpu, CreditCard, Users, ArrowLeft, RotateCcw, ExternalLink, Check,
  ArrowRight,
} from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { SEOHead } from '@/components/SEOHead';

// ─── Step definitions ────────────────────────────────────────────────────────
const STEPS = {
  category: {
    key: 'category',
    question: 'What would you like to host?',
    options: [
      { value: 'minecraft',   label: 'Minecraft Server', icon: Gamepad2,   next: 'minecraft_players' },
      { value: 'gameserver',  label: 'Other Gameserver',  icon: Gamepad2,   next: 'results' },
      { value: 'website',     label: 'Website / Web App', icon: Globe,      next: 'website_needs' },
      { value: 'vpn',         label: 'VPN',               icon: Shield,     next: 'results' },
      { value: 'voice',       label: 'Teamspeak / Voice', icon: Headphones, next: 'results' },
      { value: 'storage',     label: 'Files & Backups',   icon: HardDrive,  next: 'storage_size' },
      { value: 'domain',      label: 'Domain',            icon: Globe,      next: 'results' },
    ],
  },
  minecraft_players: {
    key: 'minecraft_players',
    question: 'How many players at the same time?',
    options: [
      { value: 'small',  label: '1–10 players',  icon: Users, desc: 'Friends & small groups', next: 'results' },
      { value: 'medium', label: '10–50 players',  icon: Users, desc: 'Small community',       next: 'results' },
      { value: 'large',  label: '50+ players',    icon: Users, desc: 'Large community',       next: 'results' },
    ],
  },
  website_needs: {
    key: 'website_needs',
    question: 'How much control do you need?',
    options: [
      { value: 'managed', label: 'Managed web hosting',    icon: Globe,  desc: 'No server management, just upload & go', next: 'results' },
      { value: 'vps',     label: 'VPS with root access',   icon: Server, desc: 'Install your own software, full control',  next: 'vps_os' },
      { value: 'root',    label: 'Dedicated root server',  icon: Cpu,    desc: 'Maximum performance, bare-metal KVM',      next: 'results' },
    ],
  },
  vps_os: {
    key: 'vps_os',
    question: 'Which operating system?',
    options: [
      { value: 'linux',   label: 'Linux',   icon: Server,  desc: 'Debian, Ubuntu, CentOS and more', next: 'vps_budget' },
      { value: 'windows', label: 'Windows', icon: Monitor, desc: 'Remote Desktop access (RDP)',     next: 'results' },
    ],
  },
  vps_budget: {
    key: 'vps_budget',
    question: 'What is your monthly budget?',
    options: [
      { value: 'micro',  label: 'Under €3',   icon: CreditCard, desc: 'Entry level',         next: 'results' },
      { value: 'low',    label: '€3 – €10',   icon: CreditCard, desc: 'Good value',          next: 'results' },
      { value: 'medium', label: '€10 – €25',  icon: CreditCard, desc: 'More power',          next: 'results' },
      { value: 'high',   label: 'Over €25',   icon: CreditCard, desc: 'Maximum performance', next: 'results' },
    ],
  },
  storage_size: {
    key: 'storage_size',
    question: 'How much storage do you need?',
    options: [
      { value: 'small',  label: 'Up to 1 TB',  icon: HardDrive, desc: 'Backups & small archives', next: 'results' },
      { value: 'medium', label: '1 – 3 TB',    icon: HardDrive, desc: 'Media & large backups',   next: 'results' },
      { value: 'large',  label: 'Over 3 TB',   icon: HardDrive, desc: 'Large data collections',  next: 'results' },
    ],
  },
};

// ─── Result catalog ───────────────────────────────────────────────────────────
const PRODUCTS = {
  mc_small: {
    name: 'Minecraft – KVM FLEX',
    category: 'Gameserver',
    desc: 'Entry-level KVM server – perfect for 1–10 friends. Install Spigot or Paper and start playing.',
    price: 'from €1.00/month',
    url: '/vps/kvm',
    score: 95,
  },
  mc_medium: {
    name: 'Minecraft Server (2–4 GB RAM)',
    category: 'Gameserver',
    desc: 'Scale your Minecraft server to 2–4 GB RAM. Handles 10–50 players with mods and plugins.',
    price: 'from €1.50/GB RAM/month',
    url: '/gameservers/minecraft',
    score: 95,
  },
  mc_large: {
    name: 'Minecraft Server (6+ GB RAM)',
    category: 'Gameserver',
    desc: 'Large Minecraft community with 50+ players, heavy mod packs and BungeeCord network.',
    price: 'from €1.50/GB RAM/month',
    url: '/gameservers/minecraft',
    score: 90,
  },
  mc_root: {
    name: 'Linux KVM Rootserver – Medium Gen8',
    category: 'Rootserver',
    desc: 'For large Minecraft communities or BungeeCord networks. Full root access, AMD Epyc, 8 GB RAM.',
    price: 'from €10.00/month',
    url: '/rootserver/linux',
    score: 80,
  },
  gameserver: {
    name: 'Gameserver',
    category: 'Gameserver',
    desc: 'Over 20 supported games: Rust, Valheim, ARK, CS2, 7 Days to Die, Garry\'s Mod and more.',
    price: 'from €1.50/month',
    url: '/gameservers',
    score: 95,
  },
  webhosting: {
    name: 'Web Hosting SSD',
    category: 'Web Hosting',
    desc: 'Managed hosting with Plesk panel, daily backups and one-click app installer. No server knowledge needed.',
    price: 'from €X/month',
    url: '/webhosting',
    score: 95,
  },
  vps_flex: {
    name: 'KVM VPS – FLEX',
    category: 'VPS',
    desc: 'The most affordable VPS: 1 vCore, 1 GB RAM, 10 GB NVMe. Entry point for web applications.',
    price: 'from €1.00/month',
    url: '/vps/kvm',
    score: 90,
  },
  vps_ryzen_s: {
    name: 'KVM VPS Ryzen S',
    category: 'VPS',
    desc: 'AMD Ryzen 3.7 GHz+, 2 GB RAM, 25 GB NVMe – fast VPS for small web apps and APIs.',
    price: 'from €2.35/month',
    url: '/vps/kvm-ryzen',
    score: 92,
  },
  vps_ryzen_m: {
    name: 'KVM VPS Ryzen M',
    category: 'VPS',
    desc: 'AMD Ryzen 3.7 GHz+, 6 GB RAM, 50 GB NVMe – our bestseller for web apps and Node/PHP projects.',
    price: 'from €4.95/month',
    url: '/vps/kvm-ryzen',
    score: 95,
  },
  vps_windows: {
    name: 'Windows VPS – Starter',
    category: 'VPS',
    desc: 'Windows Server 2019 included, 2 vCores, 2 GB RAM – manage via Remote Desktop (RDP).',
    price: 'from €6.65/month',
    url: '/vps/windows',
    score: 95,
  },
  root_linux_small: {
    name: 'Linux KVM Rootserver – Small Gen8',
    category: 'Rootserver',
    desc: 'AMD Epyc KVM rootserver with 2 vCores, 4 GB ECC RAM and 50 GB NVMe. Full SSH access.',
    price: 'from €4.95/month',
    url: '/rootserver/linux',
    score: 90,
  },
  root_linux_expert: {
    name: 'Linux KVM Rootserver – Expert Gen8',
    category: 'Rootserver',
    desc: 'Our bestseller: 8 AMD Epyc vCores, 16 GB ECC RAM, 400 GB NVMe. Full root access.',
    price: 'from €20.00/month',
    url: '/rootserver/linux',
    score: 92,
  },
  vpn: {
    name: 'VPN Service',
    category: 'VPN',
    desc: '38 global locations, unlimited traffic, 5 devices, OpenVPN/IKEv2. No logs policy.',
    price: 'from €3.50/month',
    url: '/vpn',
    score: 98,
  },
  voice: {
    name: 'Teamspeak 3 Server',
    category: 'Voice',
    desc: 'Up to 300 slots, Frankfurt location, DDoS protected. Online within 120 seconds.',
    price: 'from €1.90/month',
    url: '/teamspeak',
    score: 98,
  },
  storage_small: {
    name: 'Storage VPS – Starter',
    category: 'VPS',
    desc: '1,000 GB HDD in RAID, 2 vCores, 1 GB RAM. Perfect for Nextcloud or offsite backups.',
    price: '€7.00/month',
    url: '/vps/storage',
    score: 95,
  },
  storage_medium: {
    name: 'Storage VPS – Small',
    category: 'VPS',
    desc: '2,000 GB HDD in RAID, 2 vCores, 2 GB RAM. Great for media archives and team backups.',
    price: '€12.50/month',
    url: '/vps/storage',
    score: 93,
  },
  storage_large: {
    name: 'Storage VPS – Medium/Large',
    category: 'VPS',
    desc: '3,000–5,000 GB HDD in RAID. For large data collections, team storage, or NAS replacement.',
    price: 'from €20.00/month',
    url: '/vps/storage',
    score: 90,
  },
  domain: {
    name: 'Domain Registration',
    category: 'Domain',
    desc: 'Register .de, .com, .net and many more TLDs. Manage DNS, redirect, and email forwarding.',
    price: 'from €X/year',
    url: '/domains',
    score: 98,
  },
};

// ─── Rule engine ─────────────────────────────────────────────────────────────
function computeResults(answers) {
  const { category, minecraft_players, website_needs, vps_os, vps_budget, storage_size } = answers;

  if (category === 'vpn')        return [PRODUCTS.vpn];
  if (category === 'voice')      return [PRODUCTS.voice];
  if (category === 'domain')     return [PRODUCTS.domain];
  if (category === 'gameserver') return [PRODUCTS.gameserver];

  if (category === 'minecraft') {
    if (minecraft_players === 'small')  return [PRODUCTS.mc_small, PRODUCTS.mc_medium];
    if (minecraft_players === 'medium') return [PRODUCTS.mc_medium, PRODUCTS.vps_ryzen_s];
    return [PRODUCTS.mc_large, PRODUCTS.mc_root, PRODUCTS.vps_ryzen_m];
  }

  if (category === 'storage') {
    if (storage_size === 'small')  return [PRODUCTS.storage_small, PRODUCTS.storage_medium];
    if (storage_size === 'medium') return [PRODUCTS.storage_medium, PRODUCTS.storage_large];
    return [PRODUCTS.storage_large];
  }

  if (category === 'website') {
    if (website_needs === 'managed') return [PRODUCTS.webhosting];
    if (website_needs === 'root')    return [PRODUCTS.root_linux_small, PRODUCTS.root_linux_expert];
    if (website_needs === 'vps') {
      if (vps_os === 'windows') return [PRODUCTS.vps_windows];
      // Linux VPS – budget based
      if (vps_budget === 'micro')  return [PRODUCTS.vps_flex, PRODUCTS.vps_ryzen_s];
      if (vps_budget === 'low')    return [PRODUCTS.vps_ryzen_s, PRODUCTS.vps_flex];
      if (vps_budget === 'medium') return [PRODUCTS.vps_ryzen_m, PRODUCTS.root_linux_small];
      return [PRODUCTS.root_linux_expert, PRODUCTS.vps_ryzen_m];
    }
  }

  return [PRODUCTS.vps_ryzen_s]; // fallback
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function ProductFinderPage() {
  const { t } = useTranslation();
  const [currentStepKey, setCurrentStepKey] = useState('category');
  const [answers, setAnswers] = useState({});
  const [history, setHistory] = useState([]);
  const [results, setResults] = useState(null);

  const step = STEPS[currentStepKey];
  const allStepKeys = ['category', 'minecraft_players', 'website_needs', 'vps_os', 'vps_budget', 'storage_size'];
  const visitedCount = history.length + 1;
  const maxSteps = 4; // worst case depth

  function choose(option) {
    const newAnswers = { ...answers, [currentStepKey]: option.value };
    setAnswers(newAnswers);
    setHistory([...history, currentStepKey]);

    if (option.next === 'results') {
      setResults(computeResults(newAnswers));
      setCurrentStepKey('results');
    } else {
      setCurrentStepKey(option.next);
    }
  }

  function back() {
    if (results) {
      const prev = history[history.length - 1];
      setHistory(history.slice(0, -1));
      setCurrentStepKey(prev);
      setResults(null);
    } else if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory(history.slice(0, -1));
      setCurrentStepKey(prev);
    }
  }

  function reset() {
    setCurrentStepKey('category');
    setAnswers({});
    setHistory([]);
    setResults(null);
  }

  const progress = results
    ? 100
    : Math.round(((visitedCount - 1) / maxSteps) * 100);

  return (
    <Layout>
      <SEOHead
        title="Product Finder – Find Your Perfect Server"
        description="Not sure which server to choose? Answer a few quick questions and we'll recommend the best hosting solution for your needs."
        canonical="/product-finder"
      />
      <div className="section-spacing">
        <div className="container-main max-w-3xl">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl md:text-5xl font-black mb-3">
              <span className="text-gradient">{t('productFinder.title')}</span>
            </h1>
            <p className="text-muted-foreground">
              {t('productFinder.subtitle')}
            </p>
          </motion.div>

          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>{t('productFinder.stepOf')} {Math.min(visitedCount, maxSteps)} {t('productFinder.of')} {maxSteps}</span>
              {(history.length > 0 || results) && (
                <button
                  onClick={reset}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                  data-testid="restart-btn"
                >
                  <RotateCcw className="w-3 h-3" />
                  {t('productFinder.startOver')}
                </button>
              )}
            </div>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {/* Question step */}
            {!results && step && (
              <motion.div
                key={currentStepKey}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.22 }}
              >
                {/* Back button */}
                {history.length > 0 && (
                  <button
                    onClick={back}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
                    data-testid="back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t('productFinder.back')}
                  </button>
                )}

                <h2 className="text-2xl font-bold mb-6">{step.question}</h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  {step.options.map((option) => (
                    <motion.button
                      key={option.value}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => choose(option)}
                      className="group text-left p-5 glass rounded-xl border border-border hover:border-primary/60 hover:bg-primary/5 transition-all duration-200 flex items-start gap-4"
                      data-testid={`option-${option.value}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                        <option.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm group-hover:text-primary transition-colors">
                          {option.label}
                        </div>
                        {option.desc && (
                          <div className="text-xs text-muted-foreground mt-0.5">{option.desc}</div>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0 mt-0.5" />
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Results */}
            {results && (
              <motion.div
                key="results"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.22 }}
              >
                <button
                  onClick={back}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
                  data-testid="back-from-results-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('productFinder.back')}
                </button>

                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">
                    {results.length === 1 ? t('productFinder.recommendation') : t('productFinder.topPicks')}
                  </h2>
                  <Badge className="bg-green-500/10 text-green-500 border-green-500/20 border">
                    <Check className="w-3.5 h-3.5 mr-1" />
                    {results.length} {results.length !== 1 ? t('productFinder.match') : t('productFinder.matchSingle')}
                  </Badge>
                </div>

                <div className="space-y-4 mb-10">
                  {results.map((product, i) => (
                    <motion.div
                      key={product.name}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className={`glass rounded-xl border p-6 ${
                        i === 0 ? 'border-primary/50 bg-primary/5' : 'border-border'
                      }`}
                      data-testid={`result-card-${i}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary" className="text-xs">{product.category}</Badge>
                            {i === 0 && (
                              <Badge className="bg-primary text-xs">{t('productFinder.bestMatch')}</Badge>
                            )}
                          </div>
                          <h3 className="font-black text-lg">{product.name}</h3>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs text-muted-foreground">Match</div>
                          <div className="text-2xl font-black text-primary">{product.score}%</div>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                        {product.desc}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-primary">{product.price}</span>
                        <Link to={product.url}>
                          <Button
                            className={`font-bold ${i === 0 ? 'bg-primary hover:bg-primary/90 glow-hover' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
                            data-testid={`result-cta-${i}`}
                          >
                            {t('productFinder.viewProduct')}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Not sure? */}
                <div className="glass rounded-xl border border-border p-6 text-center">
                  <p className="text-sm font-semibold mb-1">{t('productFinder.notSureTitle')}</p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {t('productFinder.notSureDesc')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Button variant="outline" onClick={reset} size="sm" className="border-primary/30 hover:border-primary">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {t('productFinder.startOver')}
                    </Button>
                    <a href="https://deinserverhost.de/store/submitticket.php" target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="bg-primary hover:bg-primary/90">
                        {t('productFinder.contactSupport')}
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>
    </Layout>
  );
}
